import React, { useEffect, useMemo } from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import useStyles from './FindingItem.styles';
import { Image } from '../../../../common/components/images';
import { formatDate } from '../../../../common/formatters/timeFormatters';
import { FindingShape } from '../../../models/Finding';
import { Select } from '../../../../common/components/inputs';
import useAgent from '../../../../common/hooks/useAgent';
import findingAgent from '../../../agents/findingAgent';
import FindingItemType from './type/FindingItemType';
import labelEnum from '../../../models/labelEnum';
import { useAbilityContext } from '../../../../common/contexts/abilityContext';
import { useFindingContext } from '../../../contexts/findingContext';
import { ClientCategoryShape } from '../../../../client/models/ClientCategory';
import { adminOnlyLabelFiltersEnum } from '../../../models/findingLabelFilterEnum';
import enTranslations from '../../../__i18n__/en';
import frTranslations from '../../../__i18n__/fr';
import { Icon } from '../../../../common/components/icons';
import { IconButton } from '../../../../common/components/buttons';

const FindingItem = ({ patrolId, finding, onFindingUpdate, style, isVisible, clientCategories }) => {
  const classes = useStyles();

  const { setClickedFinding } = useFindingContext();

  const [updateFinding, updatedFinding, isUpdatingFinding] = useAgent(findingAgent.updateFinding);
  const [updateFindingType, updatedFindingType, isUpdatingFindingType] = useAgent(findingAgent.updateFindingType);

  const labelOptions = clientCategories
    .filter((category) => category.source === 'prediction' || category.source === 'prediction|manual' || category.source === 'manual')
    .map((category) => ({
      value: category.key,
      label: i18n.language === 'en' ? category.labelEn : category.labelFr,
    }));

  const adminCategories = Object.keys(adminOnlyLabelFiltersEnum).map((key) => ({
    value: adminOnlyLabelFiltersEnum[key],
    label: i18n.language === 'en' ? enTranslations.class?.[key] : frTranslations.class?.[key],
  }));

  labelOptions.push(...adminCategories);

  const findingLabel = useMemo(() => {
    const label = updatedFinding ? updatedFinding.label : finding.label;
    const matchingLabel = labelOptions.find((option) => option.value === label);
    if (!matchingLabel) return labelEnum.unknown;
    return label;
  }, [finding.label, updatedFinding, labelOptions]);

  const handleLabelChange = (newLabel) => {
    updateFinding(patrolId, finding.id, { patrolId, id: finding.id, label: newLabel, classification: finding.classification ? newLabel : undefined });
  };

  const handleFindingClick = () => setClickedFinding(finding);

  const handleCopyClick = async (valueToCopy) => {
    try {
      await navigator.clipboard.writeText(valueToCopy);
      toast.success(i18n.t('common.message.success.copiedToClipboard'));
    } catch (err) {
      toast.error(i18n.t('common.message.error.global'));
    }
  };

  useEffect(() => {
    if (updatedFinding) onFindingUpdate(updatedFinding);
  }, [updatedFinding, onFindingUpdate]);

  useEffect(() => {
    if (updatedFindingType) onFindingUpdate(updatedFindingType);
  }, [updatedFindingType, onFindingUpdate]);

  const context = useAbilityContext();
  const displayedFindingIdentifier = finding.uniqueId ?? finding.spitfireId;

  const classificationOptions = clientCategories
    .filter((category) => category.source === 'manual')
    .map((category) => ({
      value: category.key,
      label: i18n.language === 'en' ? category.labelEn : category.labelFr,
    }));

  return (
    <button style={{ ...style, marginBottom: '1rem' }} onClick={handleFindingClick} type="button">
      <div className={classes.root}>
        <div className={classes.containerLeft}>
          <div className={classes.findingImageContainer}>
            <Image patrolId={patrolId} finding={finding} isVisible={isVisible} />
          </div>

          <div className={classes.description}>
            <div className={classes.textLarge}>{i18n.t('patrol.finding.findingNumber', { number: finding.id })}</div>
            <div>
              {displayedFindingIdentifier && (
                <div className={classes.textSmall}>{i18n.t('patrol.finding.spitfireId', { number: displayedFindingIdentifier })}</div>
              )}
            </div>
            <div className={classes.containerDetection}>
              <div className={classes.textLarge}>{`${i18n.t('patrol.finding.detectionAt')} ${formatDate(finding.detectedAt, finding.timezone)}`}</div>
              <Select
                selectedValueClassName={classes.label}
                value={findingLabel}
                onChange={handleLabelChange}
                options={finding.classification ? classificationOptions : labelOptions}
                loading={isUpdatingFinding}
                disabled={isUpdatingFindingType || context.cannot('update', 'finding')}
              />
            </div>

            <div className={classes.workZone}>
              <div className={classes.textMedium}>{`${i18n.t('patrol.finding.workZone')}:`}</div>
              {finding.isWorkzone && (
                <div className={classes.workZonePositive}>
                  <span>{i18n.t('common.yes')}</span>
                </div>
              )}
              {!finding.isWorkzone && (
                <div className={classes.workZoneNegative}>
                  <span>{i18n.t('common.no')}</span>
                </div>
              )}
            </div>

            <div className={classes.position}>
              <div className={classes.textSmall}>{`${i18n.t('patrol.finding.latitude')}: ${finding.coordinate.latitude.toFixed(5)}`}</div>
              <div className={classes.textSmall}>{`${i18n.t('patrol.finding.longitude')}: ${finding.coordinate.longitude.toFixed(5)}`}</div>
            </div>

            {finding.irthExternalId && (
              <div className={classes.irthExternalId}>
                <div className={classes.textSmall}>{`${i18n.t('patrol.finding.irthExternalId')}: ${finding.irthExternalId}`}</div>
                <IconButton onClick={() => handleCopyClick(finding.irthExternalId)}>
                  <Icon icon="content_copy" />
                </IconButton>
              </div>
            )}
          </div>

          {finding.contactJoined && (
            <div className={classes.contact}>
              <div>{`${i18n.t('patrol.finding.contacted')}: ${finding.contactJoined.name} ${finding.contactJoined.phoneNumber}`}</div>
              <div>{finding.contactJoined.email}</div>
            </div>
          )}
        </div>
        <div className={classes.containerRight}>
          <div className={classes.sentIndicator}>
            <div className={classes.textItalic}>
              {finding.notificationSentAt && `${i18n.t('patrol.finding.sent')}: ${formatDate(finding.notificationSentAt, finding.timezone)}`}
            </div>
            <FindingItemType
              patrolId={patrolId}
              finding={finding}
              onFindingTypeChange={updateFindingType}
              updatedFindingType={updatedFindingType}
              isLoading={isUpdatingFindingType}
              disabled={isUpdatingFinding}
            />
          </div>
        </div>
      </div>
    </button>
  );
};

FindingItem.propTypes = {
  patrolId: PropTypes.number.isRequired,
  onFindingUpdate: PropTypes.func.isRequired,
  clientCategories: PropTypes.arrayOf(ClientCategoryShape).isRequired,
  finding: FindingShape.isRequired,
  isVisible: PropTypes.bool.isRequired,
  style: PropTypes.shape({}).isRequired,
};

FindingItem.defaultProps = {};

export default FindingItem;
